import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { PageHeader, PageBody } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Sparkles, FileText, Trash2, Plus } from "lucide-react";

export default function TemplatesPage() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", subject: "", html: "", description: "" });

  const load = () => api.get("/templates").then(({ data }) => setItems(data));
  useEffect(() => {
    load();
  }, []);

  const openNew = () => {
    setEditing(null);
    setForm({ name: "", subject: "", html: "<p>Ciao {{first_name}},</p>", description: "" });
    setOpen(true);
  };
  const openEdit = (t) => {
    setEditing(t);
    setForm({
      name: t.name,
      subject: t.subject,
      html: t.html,
      description: t.description || "",
    });
    setOpen(true);
  };
  const save = async () => {
    if (!form.name || !form.subject) return toast.error("Nome e oggetto sono obbligatori");
    try {
      if (editing) {
        await api.patch(`/templates/${editing.id}`, form);
      } else {
        await api.post("/templates", form);
      }
      toast.success("Template salvato");
      setOpen(false);
      load();
    } catch (err) {
      toast.error(err?.friendlyMessage || "Errore");
    }
  };
  const del = async (id) => {
    if (!window.confirm("Eliminare questo template?")) return;
    await api.delete(`/templates/${id}`);
    toast.success("Template eliminato");
    load();
  };
  const applyTemplate = (t) => {
    sessionStorage.setItem(
      "nl_prefill_campaign",
      JSON.stringify({ subject: t.subject, html: t.html, name: t.name })
    );
    navigate("/campagne/nuova");
  };
  const seed = async () => {
    try {
      const { data } = await api.post("/templates/seed-defaults");
      if (data.seeded) {
        toast.success("3 template di esempio aggiunti");
      } else {
        toast.info("Hai già dei template");
      }
      load();
    } catch (err) {
      toast.error("Errore");
    }
  };

  return (
    <>
      <PageHeader
        title="Template"
        subtitle="Layout pronti e personalizzati per le tue newsletter."
        testid="templates-header"
        actions={
          <>
            <Button variant="outline" onClick={seed} data-testid="seed-templates">
              <Sparkles className="w-4 h-4 mr-2" /> Aggiungi esempi
            </Button>
            <Button onClick={openNew} data-testid="new-template-btn">
              <Plus className="w-4 h-4 mr-2" /> Nuovo template
            </Button>
          </>
        }
      />
      <PageBody>
        {items.length === 0 && (
          <div className="border border-dashed border-border bg-white p-16 text-center">
            <FileText className="w-8 h-8 mx-auto text-muted-foreground mb-4" strokeWidth={1.25} />
            <h2 className="font-heading text-xl mb-2">Nessun template ancora</h2>
            <p className="text-sm text-muted-foreground mb-6">
              Parti con 3 template pre-costruiti oppure creane uno da zero.
            </p>
            <div className="flex gap-2 justify-center">
              <Button onClick={seed} data-testid="empty-seed-templates">
                <Sparkles className="w-4 h-4 mr-2" /> Aggiungi esempi
              </Button>
              <Button variant="outline" onClick={openNew}>
                Crea da zero
              </Button>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map((t) => (
            <div
              key={t.id}
              className="border border-border bg-white flex flex-col overflow-hidden"
              data-testid={`template-card-${t.id}`}
            >
              <div className="border-b border-border h-40 overflow-hidden bg-muted">
                <iframe
                  title={t.name}
                  srcDoc={t.html}
                  className="w-full h-full pointer-events-none"
                  style={{ transform: "scale(0.6)", transformOrigin: "top left", width: "166%", height: "166%" }}
                />
              </div>
              <div className="p-4 flex-1 flex flex-col">
                <div className="font-heading text-lg">{t.name}</div>
                {t.description && (
                  <div className="text-xs text-muted-foreground mt-1 line-clamp-2">
                    {t.description}
                  </div>
                )}
                <div className="mt-3 flex items-center gap-2 flex-wrap">
                  <Button
                    size="sm"
                    onClick={() => applyTemplate(t)}
                    data-testid={`use-template-${t.id}`}
                  >
                    Usa
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => openEdit(t)}
                    data-testid={`edit-template-${t.id}`}
                  >
                    Modifica
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="ml-auto"
                    onClick={() => del(t.id)}
                    data-testid={`delete-template-${t.id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </PageBody>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="template-dialog">
          <DialogHeader>
            <DialogTitle>
              {editing ? "Modifica template" : "Nuovo template"}
            </DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-3">
              <div>
                <Label>Nome *</Label>
                <Input
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  data-testid="template-name-input"
                />
              </div>
              <div>
                <Label>Descrizione</Label>
                <Input
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  data-testid="template-desc-input"
                />
              </div>
              <div>
                <Label>Oggetto *</Label>
                <Input
                  value={form.subject}
                  onChange={(e) => setForm({ ...form, subject: e.target.value })}
                  data-testid="template-subject-input"
                />
              </div>
              <div>
                <Label>HTML</Label>
                <Textarea
                  className="editor-code min-h-[280px]"
                  value={form.html}
                  onChange={(e) => setForm({ ...form, html: e.target.value })}
                  data-testid="template-html-input"
                />
              </div>
            </div>
            <div className="border border-border bg-muted min-h-[400px]">
              <iframe
                title="preview"
                srcDoc={form.html}
                className="w-full h-full min-h-[400px] bg-white"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>
              Annulla
            </Button>
            <Button onClick={save} data-testid="template-save">
              Salva template
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
